import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { getApiErrorMessage } from '../utils/errorHandling';
import {
  documentsApi,
  Document,
  CreateDocumentDto,
  UpdateDocumentDto,
  GetDocumentsDto,
  DocumentStats,
  DocumentType,
} from '../api/documents';

interface UseDocumentsReturn {
  // Data
  documents: Document[];
  statistics: DocumentStats | null;
  loading: boolean;
  error: string | null;
  
  // Operations
  createDocument: (data: CreateDocumentDto | FormData) => Promise<Document | null>;
  updateDocument: (id: string, data: UpdateDocumentDto) => Promise<Document | null>;
  deleteDocument: (id: string) => Promise<boolean>;
  refreshData: () => Promise<void>;
  
  // Utility functions
  getDocumentById: (id: string) => Document | undefined;
  getDocumentsByBird: (birdId: string) => Document[];
  getDocumentsByHealthTest: (healthTestId: string) => Document[];
  getDocumentsByType: (type: DocumentType) => Document[];
}

export const useDocuments = (initialFilters: GetDocumentsDto = {}): UseDocumentsReturn => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [statistics, setStatistics] = useState<DocumentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use ref to track current filters without causing dependency loops
  const currentFiltersRef = useRef(initialFilters);
  currentFiltersRef.current = initialFilters;

  // Load documents data - stable function reference
  const loadDocuments = useCallback(async (filters: GetDocumentsDto = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const documents = await documentsApi.getDocuments(filters);
      setDocuments(documents);
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to load documents');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load statistics - stable function reference
  const loadStatistics = useCallback(async () => {
    try {
      const stats = await documentsApi.getStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Failed to load document statistics:', err);
    }
  }, []);

  // Create stable function references for operations
  const loadStatisticsRef = useRef(loadStatistics);
  loadStatisticsRef.current = loadStatistics;

  // Create document
  const createDocument = useCallback(async (data: CreateDocumentDto | FormData): Promise<Document | null> => {
    try {
      const newDocument = await documentsApi.createDocument(data);
      setDocuments(prev => [newDocument, ...prev]);
      toast.success('Document created successfully');
      await loadStatistics();
      return newDocument;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to create document');
      toast.error(errorMessage);
      throw err;
    }
  }, [loadStatistics]);

  // Update document
  const updateDocument = useCallback(async (id: string, data: UpdateDocumentDto): Promise<Document | null> => {
    try {
      const updatedDocument = await documentsApi.updateDocument(id, data);
      setDocuments(prev => 
        prev.map(document => 
          document.id === id ? updatedDocument : document
        )
      );
      toast.success('Document updated successfully');
      await loadStatistics();
      return updatedDocument;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to update document');
      toast.error(errorMessage);
      throw err;
    }
  }, [loadStatistics]);

  // Delete document
  const deleteDocument = useCallback(async (id: string): Promise<boolean> => {
    try {
      await documentsApi.deleteDocument(id);
      setDocuments(prev => prev.filter(document => document.id !== id));
      toast.success('Document deleted successfully');
      await loadStatistics();
      return true;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to delete document');
      toast.error(errorMessage);
      return false;
    }
  }, [loadStatistics]);

  // Create stable function references
  const loadDocumentsRef = useRef(loadDocuments);
  loadDocumentsRef.current = loadDocuments;

  // Refresh data - stable function reference
  const refreshData = useCallback(async () => {
    await Promise.all([
      loadDocumentsRef.current(currentFiltersRef.current),
      loadStatisticsRef.current(),
    ]);
  }, []);

  // Utility functions
  const getDocumentById = useCallback((id: string): Document | undefined => {
    return documents.find(document => document.id === id);
  }, [documents]);

  const getDocumentsByBird = useCallback((birdId: string): Document[] => {
    return documents.filter(document => document.birdId === birdId);
  }, [documents]);

  const getDocumentsByHealthTest = useCallback((healthTestId: string): Document[] => {
    return documents.filter(document => document.healthTestId === healthTestId);
  }, [documents]);

  const getDocumentsByType = useCallback((type: DocumentType): Document[] => {
    return documents.filter(document => document.type === type);
  }, [documents]);

  // Load data on mount - only runs once
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        loadDocuments(),
        loadStatistics(),
      ]);
    };
    
    loadInitialData();
  }, []); // Empty dependency array - only run on mount

  return {
    // Data
    documents,
    statistics,
    loading,
    error,
    
    // Operations
    createDocument,
    updateDocument,
    deleteDocument,
    refreshData,
    
    // Utility functions
    getDocumentById,
    getDocumentsByBird,
    getDocumentsByHealthTest,
    getDocumentsByType,
  };
}; 